import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { Task } from "../api";
import { go, loadThread, send, useStore } from "../store";
import { api } from "../api";
import { editLast, ScheduledBar, Typing } from "./ChatBits";
import { dueLabel, isLate } from "../text";
import { Composer } from "./Composer";
import { continues, MessageItem, type Item } from "./MessageItem";
import { Avatar, Check, dmPartner, isOwner, roomTitle, toast, useMe } from "./ui";

const order = (a: Task, b: Task) => {
  const k = (t: Task) => (isLate(t) ? 0 : t.due ? 1 : 2);
  return k(a) - k(b) || (a.due ?? "").localeCompare(b.due ?? "") || a.id - b.id;
};

function Card({ title, color, tasks, who }: { title: string; color: string; tasks: Task[]; who?: import("../api").User }) {
  const users = useStore((s) => s.users);
  if (!tasks.length) return null;
  return (
    <div className="card" style={{ ["--c" as any]: color }}>
      <div className="ct">{who ? <Avatar user={who} size={16} /> : <i />}{title}</div>
      {tasks.map((t) => (
        <div key={t.id} className={`row${t.status === "done" ? " done" : ""}`} onClick={() => go({ v: "task", id: t.id })}>
          <Check task={t} />
          <div className="x">{t.title}<small><Avatar user={users[t.ownerId]} size={14} />{users[t.ownerId]?.name}{t.status === "doing" ? " · in progress" : ""}</small></div>
          <span className={`d${isLate(t) ? " late" : ""}`}>{dueLabel(t.due)}{t.repeat ? " ↻" : ""}</span>
        </div>
      ))}
    </div>
  );
}

/** The tray: what's on you; in a DM, Karol also sees that person's tasks first. */
export function Tray({ roomId }: { roomId: number | null }) {
  const me = useMe();
  const tasks = useStore((s) => s.tasks);
  const users = useStore((s) => s.users);
  const room = useStore((s) => (roomId ? s.rooms[roomId] : undefined));
  const open = useMemo(() => Object.values(tasks).filter((t) => t.status !== "done").sort(order), [tasks]);
  const mine = open.filter((t) => t.ownerId === me.id);
  const partner = isOwner(me) ? dmPartner(room, me.id) : null;
  const theirs = partner ? open.filter((t) => t.ownerId === partner) : [];

  return (
    <aside className="rail">
      <div className="h"><b>Tasks</b><span>{mine.length} yours</span></div>
      <div className="cards">
        {partner && <Card title={`${users[partner]?.name}'s tasks`} color={users[partner]?.color ?? "#888"} tasks={theirs} who={users[partner]} />}
        {mine.length ? <Card title={isOwner(me) ? "On you" : "Your tasks"} color="#8a94a6" tasks={mine} /> : <div className="nothing">Nothing on you.</div>}
      </div>
    </aside>
  );
}

/** A thread opens on the right, like Slack. */
export function ThreadPanel({ roomId, rootId }: { roomId: number; rootId: number }) {
  const me = useMe();
  const root = useStore((s) => s.byId[rootId]);
  const replies = useStore((s) => s.threads[rootId]);
  const pending = useStore((s) => s.pending);
  const room = useStore((s) => s.rooms[roomId]);
  const users = useStore((s) => s.users);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { loadThread(rootId).catch(() => toast("Couldn't open the thread")); }, [rootId]);
  // Reading the thread marks it read.
  const follow = useStore((s) => s.follows[rootId]);
  const lastReply = replies?.length ? replies[replies.length - 1].id : 0;
  useEffect(() => {
    if (!follow || !lastReply || follow.lastReadId >= lastReply || !document.hasFocus()) return;
    useStore.setState((s) => ({ follows: { ...s.follows, [rootId]: { ...follow, lastReadId: lastReply } } }));
    api.readThread(rootId, lastReply).catch(() => {});
  }, [follow, lastReply, rootId]);
  const items = useMemo<Item[]>(() => [
    ...(replies ?? []).map((m) => ({ kind: "msg" as const, m })),
    ...pending.filter((p) => p.parentId === rootId).map((p) => ({ kind: "pending" as const, p })),
  ], [replies, pending, rootId]);
  useLayoutEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [items.length]);

  const title = roomTitle(room, me.id, users);
  return (
    <aside className="rail">
      <div className="h"><b>Thread <span style={{ color: "var(--ink3)", fontWeight: 400 }}>· {title}</span></b>
        <span style={{ display: "flex", gap: 12 }}>
          <button onClick={() => api.follow(rootId, !follow).then(() => toast(follow ? "Stopped following" : "Following: you'll hear about replies")).catch(() => {})}>{follow ? "Following" : "Follow"}</button>
          <button onClick={() => go({ v: "room", id: roomId })}>Close ×</button>
        </span>
      </div>
      <ScheduledBar roomId={roomId} parentId={rootId} />
      <div className="rthread" ref={box}>
        {root && <MessageItem item={{ kind: "msg", m: root }} cont={false} inThread />}
        {replies && <div className="sep">{replies.length} {replies.length === 1 ? "reply" : "replies"}</div>}
        {items.map((it, i) => <MessageItem key={it.kind === "pending" ? it.p.clientId : it.m.id} item={it} cont={continues(items[i - 1], it, me.id)} inThread />)}
      </div>
      <Typing roomId={roomId} parentId={rootId} />
      <Composer draftKey={`thread-${rootId}`} placeholder="Reply…" autoFocus roomId={roomId} parentId={rootId}
        onEditLast={() => editLast(replies ?? [], me.id)}
        onSchedule={async (body, files, also, at) => { try { useStore.setState({ scheduled: await api.schedule({ roomId, parentId: rootId, alsoInChat: also, body, sendAt: at.toISOString(), fileIds: files.map((f) => f.id) }) }); } catch (e: any) { toast(e.message || "Couldn't schedule"); return false; } }}
        alsoOption={room?.kind === "dm" ? "Also send as direct message" : `Also send to ${title}`}
        onSend={(body, files, also) => { send({ roomId, body, files, parentId: rootId, alsoInChat: also }).catch(() => toast("Not sent")); }} />
    </aside>
  );
}
